import React, { useState, useEffect, useContext, useCallback } from "react";
import { UserContext } from "../../../contexts/user.context";
import { Get, handleError } from "../../../utils/fetch.util";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import LoadingData from "../../../components/loadingData/loadingData.component";
import {
  FiUser,
  FiTruck,
  FiLogIn,
  FiLogOut,
} from "react-icons/fi";
import Select from "react-select";
import Image from "../../../components/Image/Image.component";
import { debounce } from "lodash";

interface LogBy {
  asdc_client_user_id: number;
  asdc_client_user_name: string;
  profile_pic: string;
}

interface VisitorRequest {
  id: number;
  name: string;
  nationalId: string;
  reason: string;
  allowVehicle: boolean;
}

interface Site {
  site_id: number;
  site_name: string;
}

interface EntryLog {
  id: number;
  visitor_request_id: number;
  date: string;
  client_id: number;
  status: "entry" | "exit";
  vehicle_number: string;
  allowVehicle: boolean;
  site_id: number;
  log_by_id: number;
  visitor_request: VisitorRequest;
  log_by: LogBy;
  site: Site;
}

interface ApiResponse {
  status: string;
  data: {
    count: number;
    visitorRequestEntryLogs: EntryLog[];
  };
}

export default function VisitorEntryExitLogs() {
  const { t } = useTranslation();
  const { getValidToken, userData } = useContext(UserContext);

  // State management
  const [entryLogs, setEntryLogs] = useState<EntryLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [entryLogStatus, setEntryLogStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [activeTab, setActiveTab] = useState<"visitor" | "vehicle">("visitor");

  const sites = userData[0]?.user_sites_relationShip?.map((site: any) => site?.site) || [];
  const sitesOptions = sites.map((site: any) => ({
    value: site.site_id,
    label: site.site_name,
  }));

  const [siteId, setSiteId] = useState<{ value: number; label: string } | null>(null);

  // Fetch entry logs
  const fetchEntryLogs = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        pageItemsCount: pageSize.toString(),
        vehicle_only: activeTab === "vehicle" ? "true" : "false",
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(siteId?.value && { site_id: siteId.value.toString() }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(entryLogStatus && { entry_log_status: entryLogStatus }),
      } as any);

      const response = await Get(
        `visitor-requests/get-visitor-request-entry-logs?${queryParams}`,
        getValidToken()
      );

      if (response.error || !response.data) {
        handleError(response);
        return;
      }

      const apiResponse: ApiResponse = response;
      setEntryLogs(apiResponse.data.visitorRequestEntryLogs);
      setTotalCount(apiResponse.data.count);
    } catch (error) {
      console.error("Error fetching entry logs:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search term changes
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setDebouncedSearchTerm(term);
    }, 500),
    []
  );

  // Update debounced search term when search term changes
  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, siteId, startDate, endDate, activeTab, entryLogStatus]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchEntryLogs();
  }, [currentPage, debouncedSearchTerm, siteId, startDate, endDate, activeTab, entryLogStatus]);

  // Status badge component
  const StatusBadge = ({ status }: { status: "entry" | "exit" }) => {
    const getStatusStyles = () => {
      switch (status) {
        case "entry":
          return "bg-green-100 text-green-800";
        case "exit":
          return "bg-red-100 text-red-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    };

    const getStatusIcon = () => {
      switch (status) {
        case "entry":
          return <FiLogIn className="w-4 h-4 mr-1" />;
        case "exit":
          return <FiLogOut className="w-4 h-4 mr-1" />;
        default:
          return null;
      }
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyles()}`}
      >
        {getStatusIcon()}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Entry log card component
  const EntryLogCard = ({ log }: { log: EntryLog }) => {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4 border border-gray-100">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
              {log.log_by.profile_pic ? (
                <Image
                  src={log.log_by.profile_pic}
                  alt={log.log_by.asdc_client_user_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-lg font-semibold text-gray-600">
                    {log.log_by.asdc_client_user_name.charAt(0)}
                  </span>
                </div>
              )}
            </div>
            <div>
              <h3 className="text-lg font-medium text-blue-600">
                {log.log_by.asdc_client_user_name}
              </h3>
              <p className="text-sm text-gray-500">
                {log.status === "entry" ? "Entry to" : "Exit from"} {log.site.site_name}
              </p>
            </div>
          </div>
          <StatusBadge status={log.status} />
        </div>

        <div className="mt-3 text-sm text-gray-600">
          <p>
            <span className="font-medium">
              {log.log_by.asdc_client_user_name} approved {activeTab === "visitor" ? "Visitor" : "Vehicle"} {log.visitor_request.name}
            </span>
          </p>
          {log.vehicle_number && (
            <p>
              <span className="font-medium">Vehicle Number:</span> {log.vehicle_number}
            </p>
          )}
          <p>
            <span className="font-medium">National ID:</span> {log.visitor_request.nationalId}
          </p>
          {log.visitor_request.reason && (
            <p>
              <span className="font-medium">Visit Reason:</span> {log.visitor_request.reason}
            </p>
          )}
          <p>
            <span className="font-medium">Date:</span> {format(new Date(log.date), "MMM dd, yyyy HH:mm")}
          </p>
        </div>
      </div>
    );
  };

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center mt-6 mb-4">
        <nav className="flex items-center space-x-1">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 disabled:opacity-50"
          >
            Previous
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => setCurrentPage(1)}
                className={`px-3 py-1 rounded-md ${
                  currentPage === 1
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                1
              </button>
              {startPage > 2 && (
                <span className="px-2 py-1 text-gray-500">...</span>
              )}
            </>
          )}

          {pageNumbers.map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 rounded-md ${
                currentPage === page
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {page}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="px-2 py-1 text-gray-500">...</span>
              )}
              <button
                onClick={() => setCurrentPage(totalPages)}
                className={`px-3 py-1 rounded-md ${
                  currentPage === totalPages
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 disabled:opacity-50"
          >
            Next
          </button>
        </nav>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">Visitor Entry/Exit Logs</h1>

      {/* Tabs */}
      <div className="flex mb-4 border-b">
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === "visitor"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("visitor")}
        >
          <FiUser className="inline mr-2" />
          Visitor
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === "vehicle"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("vehicle")}
        >
          <FiTruck className="inline mr-2" />
          Vehicle
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            type="text"
            id="search"
            placeholder="Search by visitor name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="site" className="block text-sm font-medium text-gray-700 mb-1">
            Select a Site
          </label>
          <Select
            id="site"
            options={sitesOptions}
            value={siteId}
            onChange={(option) => setSiteId(option)}
            isClearable
            placeholder="Select a site"
            className="basic-single"
            classNamePrefix="select"
          />
        </div>

        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
            Entry/Exit Status
          </label>
          <Select
            id="status"
            options={[
              { value: "entry", label: "Entry" },
              { value: "exit", label: "Exit" },
            ]}
            value={
              entryLogStatus
                ? {
                    value: entryLogStatus,
                    label: entryLogStatus.charAt(0).toUpperCase() + entryLogStatus.slice(1),
                  }
                : null
            }
            onChange={(option) => setEntryLogStatus(option ? option.value : "")}
            isClearable
            placeholder="Select status"
            className="basic-single"
            classNamePrefix="select"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingData />
      ) : (
        <>
          {entryLogs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No entry/exit logs found</p>
            </div>
          ) : (
            <div className="mb-6 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {entryLogs.map((log) => (
                <EntryLogCard key={log.id} log={log} />
              ))}
            </div>
          )}
          <Pagination />
        </>
      )}
    </div>
  );
}
